import angular from 'angular';
import swal from 'sweetalert2';

/*******************************************************************************
 * @ngdoc  provider
 * @name   AppServicesProvider
 * @module csm.books.InitialSetup.services
 * @description
 * Use `AppServicesProvider` to configure `AppServices`.
 *******************************************************************************/
/* @ngInject */ export function AppServicesProvider($injector) {
  this.$get = $injector.invoke(service, this);

  /**
   * @ngdoc      property
   * @name       services
   * @propertyOf csm.books.InitialSetup.services.AppServicesProvider
   * @description
   * Object of injectable functions returning service functions. Each will
   * be called with `$injector.invoke()` to create the `AppServices` methods.
   * Most of these are application specific and must be defined by the app.
   * This is a `sealed` object so no new properties can be added.
   */
  this.services = Object.seal(
    Object.defineProperties(
      {},
      {
        /**
         * @ngdoc  function
         * @name   AppServices#getSummary
         * @module csm.books.InitialSetup.services
         * @return {array{string}} Lines to display in `<summary-panel>`.
         * @description
         * Called by `<summary-panel>`. Returns lines to display.
         */
        getSummary: { writable: true },

        /**
         * @ngdoc  function
         * @name   AppServices#loadSetup
         * @module csm.books.InitialSetup.services
         * @return {object} Properties to add to application controller.
         * @description
         * Called by `<initial-setup>`. Loads and initializes setup data from the
         * server. Returns object of properties to add to controller.
         */
        loadSetup: { writable: true },

        /**
         * @ngdoc  function
         * @name   AppServices#refreshData
         * @module csm.books.InitialSetup.services
         * @description
         * Called whenever `AppData` must be "refreshed". Such as for data providers.
         */
        refreshData: { writable: true },

        /*----------------------------*/
        /* Services used by all apps. */
        /*----------------------------*/
        Alert: { value: Alert },
        enableBalanceInput: { value: enableBalanceInput },
        getBankAccountsSummary: { value: getBankAccountsSummary },
        getFundBalanceSummary: { value: getFundBalanceSummary },
        removeAccountHandler: { value: removeAccountHandler },
        serverErrorHandler: { value: serverErrorHandler }
      }
    )
  );
}

/*******************************************************************************
 * service */
/**
 * @ngdoc  service
 * @name   AppServices
 * @module csm.books.InitialSetup.services
 * @description
 * App defined methods used by setup application. Service methods are created by
 * calling `$injector.invoke()` for each `AppServicesProvider.services` property.
 *******************************************************************************/
/* @ngInject */ function service() {
  return function ($injector) {
    var services = this.services;
    var AppServices = {};
    Object.getOwnPropertyNames(services).forEach(function (name) {
      /*----------------------------------------------------*/
      /* `AppServices` object must be injected manually,    */
      /* here, to avoid a circular dependency error if      */
      /* `AppServices` is injected into one of the methods. */
      /*----------------------------------------------------*/
      if (angular.isFunction(services[name]))
        AppServices[name] = $injector.invoke(services[name], null, {
          AppServices: AppServices
        });
      else
        console.error("AppServices: service '" + name + "' not implemented.");
    });
    return AppServices;
  };
}

/*******************************************************************************
 * Alert */
/**
 * @ngdoc  service
 * @name   AppServices.Alert
 * @module csm.books.InitialSetup.services
 * @description
 * SweetAlert2 alternative alert services.
 *******************************************************************************/
/* @ngInject */ function Alert($q) {
  return {
    /**
     * @ngdoc  function
     * @name   AppServices.Alert#confirm
     * @module csm.books.InitialSetup.services
     * @param  {string}  title       Dialog title.
     * @param  {string}  html        HTML content.
     * @param  {string=} okText      Ok button text, default='Ok'.
     * @param  {string=} cancelText  Cancel button text, default='Cancel'
     * @param  {object=} options     Additional SweetAlert2 options.
     * @return {promise} Ok button resolves, Cancel button rejects.
     * @description
     * Confirm dialog without icon.
     */
    confirm: function (title, html, okText, cancelText, options) {
      return this.show(
        title,
        html,
        Object.assign(
          {
            confirmButtonText: okText || 'Ok',
            cancelButtonText: cancelText || 'Cancel',
            showCancelButton: true
          },
          options
        )
      );
    },

    /**
     * @ngdoc  function
     * @name   AppServices.Alert#error
     * @module csm.books.InitialSetup.services
     * @param  {string} title Dialog title.
     * @param  {string} html  HTML content.
     * @description
     * Error notification dialog.
     */
    error: function (title, html) {
      return this.show(title, html, { type: 'error' });
    },

    /**
     * @ngdoc  function
     * @name   AppServices.Alert#reverseConfirm
     * @module csm.books.InitialSetup.services
     * @param  {string}  title       Dialog title.
     * @param  {string}  html        HTML content.
     * @param  {string=} okText      Ok button text, default='Ok'.
     * @param  {string=} cancelText  Cancel button text, default='Cancel'
     * @return {promise} Ok button rejects, Cancel button resolves.
     * @description
     * Confirm dialog with reversed buttons. Cancel resolves, Ok rejects.
     */
    reverseConfirm: function (title, html, okText, cancelText) {
      return this.confirm(title, html, okText, cancelText, {
        cancelButtonColor: '#d9534f'
      }).then(
        function () {
          return $q.reject();
        },
        function (reason) {
          return reason === 'cancel' ? $q.resolve() : $q.reject();
        }
      );
    },

    /**
     * @ngdoc  function
     * @name   AppServices.Alert#show
     * @module csm.books.InitialSetup.services
     * @param  {string}  title       Dialog title.
     * @param  {string}  html        HTML content.
     * @param  {object=} options     Additional SweetAlert2 options.
     * @return {promise} Returned from SweetAlert2.
     * @description
     * Shows SweetAlert2 dialog.
     */
    show: function (title, html, options) {
      /*---------------------------------------------------------------------*/
      /* It is unclear why this is necessary but returning the promise from  */
      /* SweetAlert2 results in some strange behaviour. Resolve seems to     */
      /* interfere with the next digest cycle and reject throws an exception */
      /* if there is no handler. Wrapping it all in $q seems to fix it.      */
      /*---------------------------------------------------------------------*/
      return $q(function (resolve, reject) {
        swal(Object.assign({ title: title, html: html }, options || {})).then(
          resolve,
          reject
        );
      });
    },

    /**
     * @ngdoc  function
     * @name   AppServices.Alert#success
     * @module csm.books.InitialSetup.services
     * @param  {string}  title       Dialog title.
     * @param  {string}  html        HTML content.
     * @description
     * Success dialog with green Ok button.
     */
    success: function (title, html) {
      return this.show(title || 'Success!', html, {
        confirmButtonColor: '#5cb85c',
        type: 'success'
      });
    }
  };
}

/*******************************************************************************
 * enableBalanceInput */
/**
 * Returns `true` if account current balance input is allowed. Used in account
 * list grids and add/change dialogs. Allowed for Balance Sheet accounts except
 * Bank and Undeposited Funds accounts.
 *
 * @param  {SetupAccount} acctInfo  Account info.
 * @return {boolean}      Allow change flag.
 *******************************************************************************/
/* @ngInject */ function enableBalanceInput(SERVER) {
  var isUndepositedFunds = new RegExp(
    '^' + SERVER.AccountTypes.UNDEPOSITED_FUNDS,
    'i'
  );

  return function (acct) {
    return (
      acct.isBSAccount &&
      acct.type !== SERVER.AccountTypes.BANK &&
      !isUndepositedFunds.test(acct.type)
    );
  };
}

/*******************************************************************************
 * getBankAccountsSummary */
/**
 * @ngdoc  function
 * @name   AppServices#getBankAccountsSummary
 * @module csm.books.InitialSetup.services
 * @description
 * Called by `getSummary` to add lines for summary of bank accounts to `dpSummary`.
 *******************************************************************************/
/* @ngInject */ function getBankAccountsSummary(AppData) {
  return function (dpSummary) {
    AppData.bankAccounts.forEach(function (acct) {
      dpSummary.push(
        { text: acct.accountName },
        {
          text: 'Statement Balance',
          indent: true,
          amount: acct.lastStmtBalance
        },
        {
          text: 'Outstanding Checks/Deposits',
          indent: true,
          amount: acct.pendingItems.reduce(function (total, item) {
            return total + item.change;
          }, 0)
        },
        {
          text: 'Current Balance',
          indent: true,
          amount: acct.currentBalance
        }
      );
    });
  };
}

/*******************************************************************************
 * getFundBalanceSummary */
/**
 * @ngdoc  function
 * @name   AppServices#getFundBalanceSummary
 * @module csm.books.InitialSetup.services
 * @description
 * Called by `getSummary` to add lines for Fund Balance and Change in Fund Balance
 * to `dpSummary`.
 *******************************************************************************/
/* @ngInject */ function getFundBalanceSummary(AppData) {
  return function (dpSummary) {
    /*-----------------------------------------------*/
    /* First section is always Fiscal Year. If there */
    /* are opening balances, use the calculated fund */
    /* balance, else the opening fund balance.       */
    /*-----------------------------------------------*/
    var text = (AppData.needTY ? 'Fiscal Year ' : '') + 'Fund Balance';
    var amt1 = AppData.needFY
      ? AppData.fyCalcFundBalance
      : AppData.openingFundBalance;
    var amt2 = AppData.needFY ? AppData.fyCalcNetIncome : 0;

    dpSummary.push(
      null,
      { text: text, amount: amt1, bold: true },
      { text: 'Change in Fund Balance', amount: amt2, bold: true }
    );

    /*-----------------------------------------------------*/
    /* If the user chose to adjust Fund Balance instead of */
    /* correctly entering balances, show the discrepancy.  */
    /*-----------------------------------------------------*/
    if (AppData.needFY && AppData.fyFundBalanceAdj)
      dpSummary.push(
        {
          text: 'Total Income/Expenses',
          amount: AppData.fyTotalNetIncome,
          bold: true
        },
        {
          text: 'Fund Balance Adjustment',
          amount: AppData.fyFundBalanceAdj,
          bold: true,
          red: true
        }
      );

    /*----------*/
    /* Tax Year */
    /*----------*/
    if (AppData.needTY) {
      dpSummary.push(
        null,
        {
          text: 'Tax Year Fund Balance',
          amount: AppData.tyCalcFundBalance,
          bold: true
        },
        {
          text: 'Change in Fund Balance',
          amount: AppData.tyCalcNetIncome,
          bold: true
        }
      );

      if (AppData.tyFundBalanceAdj)
        dpSummary.push(
          {
            text: 'Total Income/Expenses',
            amount: AppData.tyTotalNetIncome,
            bold: true
          },
          {
            text: 'Fund Balance Adjustment',
            amount: AppData.tyFundBalanceAdj,
            bold: true,
            red: true
          }
        );
    }
  };
}

/*******************************************************************************
 * removeAccountHandler */
/**
 * @ngdoc  function
 * @name   AppServices#removeAccountHandler
 * @module csm.books.InitialSetup.services
 * @param  {SetupAccount} acct  Account to be removed.
 * @param  {promise}      Resolved with `acct` if user confirms, else rejects.
 * @description
 * Remove button handler for accounts. Shows confirm dialog. Does not allow
 * removal of default accounts.
 *******************************************************************************/
/* @ngInject */ function removeAccountHandler($q, AppServices, SERVER) {
  return function (acct) {
    var label = acct.isBankAccount
      ? 'Bank Account'
      : SERVER.BooksWebConstants.accountLabel;

    if (!acct.userAdded)
      return AppServices.Alert.error(
        'Unable to Remove',
        '<p>This is a Default ' + label + '<br/>and cannot be removed.</p>'
      ).then(function () {
        return $q.reject();
      });

    return AppServices.Alert.confirm(
      'Remove ' + label,
      '<p>Are you sure you want to<br/>Remove this ' + label + '?'
    ).then(function () {
      return $q.resolve(acct);
    });
  };
}

/*******************************************************************************
 * serverErrorHandler */
/**
 * @ngdoc  function
 * @name   AppServices#serverErrorHandler
 * @module csm.books.InitialSetup.services
 * @description
 * Server failure handler. Shows the user a dialog and reloads the login page.
 *******************************************************************************/
/* @ngInject */ function serverErrorHandler(AppServices, BusyScreen) {
  return function () {
    BusyScreen.setBusy(false);
    AppServices.Alert.error(
      'Server Error',
      '<p>An error has occurred on the server and<br>Setup could not be completed.</p>' +
        '<p>The information you entered has been saved.</p>' +
        '<p>Please contact customer support for help.</p>'
    ).then(function () {
      location = '/';
    });
  };
}
